import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Snackbar } from '@mui/material';
import { ApiError } from '../../../lib/http/ApiError';
import {
  NotificationContext,
  type NotificationItem,
  type NotificationSeverity,
} from './NotificationContext';

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<NotificationItem | null>(null);

  const notify = useCallback((n: NotificationItem) => {
    setCurrent(n);
    setOpen(true);
  }, []);

  const notifyError = useCallback(
    (error: unknown) => {
      if (ApiError.isApiError(error)) {
        // 401 : géré par useFetch → AuthProvider (logout + redirect). On n'affiche rien.
        if (error.isUnauthorized) return;
        const severity: NotificationSeverity = error.isServerError ? 'error' : 'warning';
        notify({ message: error.messages[0] ?? t('notification.genericError'), severity });
      } else if (error instanceof Error) {
        notify({ message: error.message, severity: 'error' });
      } else {
        notify({ message: t('notification.genericError'), severity: 'error' });
      }
    },
    [notify, t],
  );

  const notifySuccess = useCallback(
    (message: string) => {
      notify({ message, severity: 'success' });
    },
    [notify],
  );

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <NotificationContext.Provider value={{ notify, notifyError, notifySuccess }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={current?.severity} onClose={handleClose} variant="filled">
          {current?.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
}
